import InlineDatePicker from '@/components/common/DatePicker/InlineDatePicker';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { FlexProps, FormLabel, Grid, InputProps } from '@chakra-ui/react';
import {
    AmountRecurrence,
    getRecurrenceConfig,
    RecurrenceFreq,
    TransactionSchedulePlan,
} from '@common/types';
import { dt } from '@common/util/dateTime';
import { parseDate } from '@internationalized/date';
import { useCallback, useState } from 'react';
import { SliderConfig } from './FormattedInput';
import InputField from './InputField';
import PlusOrMinusInput, {
    parseWithPlusOrMinus,
    PlusOrMinusValue,
} from './PlusOrMinusInput';
import TextInput from './TextInput';
import useAutofocus from './useAutofocus';

export interface TransactionScheduleInputOptions {
    disabledText?: string;
    fixedFreq?: RecurrenceFreq;
}

export interface TransactionScheduleInputProps<
    TValue extends TransactionSchedulePlan | AmountRecurrence,
> extends FlexProps,
        TransactionScheduleInputOptions {
    label?: string | React.ReactNode;
    transactionSchedule: TValue;
    onChangeTransactionSchedule: (value: TValue) => void;
    autoFocus?: boolean;
    autoFocusDelay?: number;
    amountTabIndex?: number;
    recurrenceTabIndex?: number;
    showPlusOrMinusComponent?: boolean;
    showDatePicker?: boolean;
    datePickerPosition?: 'right' | 'bottom';
    datePickerLabel?: string | React.ReactNode;
    datePickerFieldName?: 'startDate' | 'nextTransactionDate';
    datePickerValue?: string;
    plusOrMinusTabIndex?: number;
    slider?: SliderConfig;
    inputProps?: InputProps;
}

export default function TransactionScheduleInput<
    TValue extends TransactionSchedulePlan | AmountRecurrence,
>({
    label,
    transactionSchedule,
    onChangeTransactionSchedule,
    autoFocus,
    autoFocusDelay,
    amountTabIndex,
    recurrenceTabIndex,
    showPlusOrMinusComponent,
    showDatePicker,
    datePickerPosition = 'right',
    datePickerLabel,
    datePickerFieldName = 'startDate',
    datePickerValue,
    plusOrMinusTabIndex,
    slider,
    inputProps,
    disabledText,
    fixedFreq,
    ...props
}: TransactionScheduleInputProps<TValue>) {
    const [plusOrMinus, setPlusOrMinus] = useState<'+' | '-'>(
        transactionSchedule.amount >= 0 ? '+' : '-'
    );
    const handleChange = useCallback(
        (transactionSchedule: TValue, plusOrMinus: PlusOrMinusValue) => {
            {
                onChangeTransactionSchedule?.({
                    ...transactionSchedule,
                    amount: parseWithPlusOrMinus(
                        transactionSchedule.amount,
                        plusOrMinus
                    ),
                });
            }
        },
        [onChangeTransactionSchedule]
    );
    const firstInputRef = useAutofocus({ delay: autoFocusDelay });
    const isDisabled = !!disabledText;

    const inputs = (
        <Grid
            gap={4}
            templateColumns={
                showPlusOrMinusComponent ? '60px 1fr 120px' : '1fr 120px'
            }
            alignItems="flex-end"
        >
            {showPlusOrMinusComponent && (
                <PlusOrMinusInput
                    name="transactionScheduleInputPlusOrMinus"
                    value={plusOrMinus}
                    onChange={(updatedPlusOrMinus: PlusOrMinusValue) => {
                        setPlusOrMinus(updatedPlusOrMinus);
                        handleChange(transactionSchedule, updatedPlusOrMinus);
                    }}
                    variant={inputProps?.variant}
                    size={inputProps?.size}
                    tabIndex={plusOrMinusTabIndex}
                    isDisabled={isDisabled}
                />
            )}
            <InputField
                name="transactionScheduleInputAmount"
                label=""
                value={
                    disabledText
                        ? disabledText
                        : showPlusOrMinusComponent
                          ? Math.abs(transactionSchedule.amount)
                          : transactionSchedule.amount
                }
                onChange={(amount) =>
                    showPlusOrMinusComponent
                        ? handleChange(
                              { ...transactionSchedule, amount },
                              plusOrMinus
                          )
                        : onChangeTransactionSchedule({
                              ...transactionSchedule,
                              amount,
                          })
                }
                type={disabledText ? 'text' : 'currency'}
                isRequired={true}
                inputProps={inputProps}
                ref={autoFocus ? firstInputRef : null}
                tabIndex={amountTabIndex}
                slider={slider}
                isDisabled={isDisabled}
                inputOptions={{ min: 0 }}
            />
            {fixedFreq ? (
                // Hack to simulate readonly select; can't just pass readonly
                // to the select component because Chakra doesn't pass it
                // through to the caret icon
                <TextInput
                    {...inputProps}
                    value={
                        '/' +
                        getRecurrenceConfig({
                            freq: fixedFreq,
                            interval: 1,
                        }).freqString
                    }
                    onChange={() => undefined}
                    isReadOnly={true}
                />
            ) : (
                <InputField
                    name="transactionScheduleInputRecurrence"
                    label=""
                    value={transactionSchedule.recurrence}
                    onChange={(recurrence) =>
                        onChangeTransactionSchedule({
                            ...transactionSchedule,
                            recurrence,
                        })
                    }
                    type="recurrence"
                    isRequired={true}
                    inputProps={inputProps}
                    tabIndex={recurrenceTabIndex}
                    isDisabled={isDisabled}
                />
            )}
        </Grid>
    );

    const minDateStr = dt().startOf('day').toDateStr();
    const minDateValue = parseDate(minDateStr);
    const maxDateValue = parseDate(
        dt(minDateStr).add(3, 'months').endOf('month').toDateStr()
    );

    const datePicker = (
        <InlineDatePicker
            mt={datePickerPosition === 'bottom' ? 4 : 0}
            label={datePickerLabel}
            value={datePickerValue || minDateStr}
            onChange={(value) =>
                onChangeTransactionSchedule({
                    ...transactionSchedule,
                    [datePickerFieldName]: value,
                })
            }
            options={{ minValue: minDateValue, maxValue: maxDateValue }}
        />
    );

    if (!label && !showDatePicker) return inputs;

    return (
        <FlexCol {...props}>
            {(label || showDatePicker) && (
                <FlexRow color="textMuted" fontSize="sm">
                    {label && (
                        <FormLabel mb={{ base: 1, '2xl': 2 }}>
                            {label}
                        </FormLabel>
                    )}
                    {showDatePicker && datePickerPosition === 'right' && (
                        <>{datePicker}</>
                    )}
                </FlexRow>
            )}
            {inputs}
            {showDatePicker && datePickerPosition === 'bottom' && (
                <>{datePicker}</>
            )}
        </FlexCol>
    );
}
